import React, { createContext, useContext, useEffect, useState } from "react";
import { Route, Routes, Navigate } from "react-router-dom";
import { ThemeProvider } from "@mui/material/styles";
import { useAuth } from "context/react-lib/firebase-auth/AuthProvider";
import ChatView from "views/ChatView";
import ChatViewV2 from "views/ChatViewV2";
import AuthView from "views/AuthView";
import PaymentRedirectView from "views/PaymentRedirectView";
import TestView from "views/TestView";
import ToolsView from "views/ToolsView";
import FilesView from "views/FilesView";
import BookmarkedView from "views/BookmarksView";
import DocumentEditorView from "views/DocumentEditorView";
import BotLoader from "components/bot/BotLoader";
import ShareListener from "components/share/ShareListener";
import ToolLoader from "components/tools/ToolLoader";
import PopupMessage from "components/react-lib/generic-ui/PopupMessage";
import CollectionLoader from "components/react-lib/generic-ui/CollectionLoader";
import EditProfileModal from "components/users/EditProfileModal";
import CreateBotModal from "components/bot/CreateBotModal";
import { collection, query, where } from "firebase/firestore";
import { db } from "firebaseApp";
import { darkTheme, lightTheme } from "themes";
import { GlobalRefProvider } from "context/react-lib/generic-ui/GlobalRefContext";
import ShareView from "views/ShareView";
import WelcomeModal from "components/onboarding/WelcomeModal";
import TalkView from "views/TalkView";
import { useDispatch } from "react-redux";
import { popupActions } from "store/react-lib/generic-ui/popup";
import { messaging, getAndStoreToken } from "firebaseApp";
import { onMessage } from "firebase/messaging";
import AudioView from "views/AudioView";
import ImageStudioView from "./views/ImageStudioView";
import AIEditorView from "components/ai-editor/AIEditorView";
import AnnouncementsModal from "components/announcements/AnnouncementModal";
import GenerateAIMenuView from "views/GenerateAIMenuView";
import AIMenuView from "ai-menu/views/AIMenuView";

// import TokenBuilderView from "./tokens/TokenBuilderView";

const ThemeContext = createContext();

export const useThemeContext = () => useContext(ThemeContext);

// Define a private route component
function PrivateRoute({ children }) {
  const auth = useAuth();

  return auth.currentUser ? children : <Navigate to="/login" />;
}

function App() {
  const auth = useAuth();

  const dispatch = useDispatch();

  const [theme, setTheme] = useState(() => {
    const savedTheme = localStorage.getItem("theme");
    return savedTheme ? savedTheme : "dark";
  });

  const themeObject = theme === "light" ? lightTheme : darkTheme;

  const toggleTheme = () => {
    const newTheme = theme === "light" ? "dark" : "light";
    setTheme(newTheme);
    localStorage.setItem("theme", newTheme);
  };

  const init = async () => {
    onMessage(messaging, (payload) => {
      dispatch(popupActions.showInfo(payload.notification.body));
    });
  };

  useEffect(() => {
    init();
  }, []);

  useEffect(() => {
    if (auth.currentUser) {
      getAndStoreToken();
    }
  }, [auth.currentUser]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <ThemeProvider theme={themeObject}>
        <div
          id="main-container"
          style={{
            backgroundColor: themeObject.palette.background.default,
            overflow: "hidden",
          }}
        >
          <GlobalRefProvider>
            <AnnouncementsModal />
            <EditProfileModal />
            <PopupMessage />
            <BotLoader />
            <WelcomeModal />
            <CollectionLoader
              collectionName="models"
              query={collection(db, "models")}
            />
            {auth.currentUser && (
              <CollectionLoader
                collectionName="friends"
                query={query(
                  collection(db, "friends"),
                  where("uid", "==", auth.currentUser.uid)
                )}
              />
            )}
            <ToolLoader />
            <ShareListener />
            <CreateBotModal />
            {auth.currentUser && (
              <CollectionLoader
                collectionName="files"
                query={query(
                  collection(db, "files"),
                  where("uid", "==", auth.currentUser.uid)
                )}
              />
            )}
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<AuthView />} />
              <Route
                path="/payment/:status"
                element={<PaymentRedirectView />}
              />
              <Route path="/share/:shareId" element={<ShareView />} />
              {/* <Route path="/tokens" element={<TokenBuilderView />} /> */}
              {/* Private routes */}
              <Route
                path="/:conversationId?"
                element={
                  <PrivateRoute>
                    <ChatViewV2 />
                  </PrivateRoute>
                }
              />
              {/* <Route
                path="/chat/:conversationId?"
                element={
                  <PrivateRoute>
                    <ChatViewV2 />
                  </PrivateRoute>
                }
              /> */}
              <Route
                path="/tools"
                element={
                  <PrivateRoute>
                    <ToolsView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/files"
                element={
                  <PrivateRoute>
                    <FilesView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/bookmarks"
                element={
                  <PrivateRoute>
                    <BookmarkedView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/editor"
                element={
                  <PrivateRoute>
                    <DocumentEditorView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/talk"
                element={
                  <PrivateRoute>
                    <TalkView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/talk/:friendId"
                element={
                  <PrivateRoute>
                    <TalkView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/audio"
                element={
                  <PrivateRoute>
                    <AudioView />
                  </PrivateRoute>
                }
              />

              <Route
                path="/images"
                element={
                  <PrivateRoute>
                    <ImageStudioView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/docs/:documentId?"
                element={
                  <PrivateRoute>
                    <AIEditorView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/aiMenu"
                element={
                  <PrivateRoute>
                    <GenerateAIMenuView />
                  </PrivateRoute>
                }
              />
              <Route
                path="/aiMenu/:menuId"
                element={
                  <PrivateRoute>
                    <AIMenuView />
                  </PrivateRoute>
                }
              />
            </Routes>
          </GlobalRefProvider>
        </div>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App;
